import { useState } from "react";

export default function PlatformSettings() {
  const [activeTab, setActiveTab] = useState<
    "general" | "payments" | "notifications" | "security"
  >("general");

  const [settings, setSettings] = useState({
    general: {
      platformName: "Instapay VTU Platform",
      platformDescription: "Fast, Reliable & Rewarding VTU Services",
      supportEmail: "support@instapay.ng",
      supportPhone: "+234 8030591549",
      currency: "NGN",
      timezone: "Africa/Lagos",
      maintenanceMode: false,
      userRegistration: true,
    },
    payments: {
      minimumWalletFunding: 100,
      maximumWalletFunding: 500000,
      transactionFee: 0.5,
      paymentGateways: {
        paystack: true,
        flutterwave: true,
        bankTransfer: true,
      },
      autoProcessPayments: true,
      refundPolicy: 24,
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: true,
      pushNotifications: true,
      transactionAlerts: true,
      maintenanceAlerts: true,
      marketingEmails: false,
    },
    security: {
      twoFactorAuth: true,
      sessionTimeout: 30,
      passwordExpiry: 90,
      loginAttempts: 5,
      ipWhitelist: "",
      encryptionLevel: "AES-256",
      backupFrequency: "daily",
    },
  });

  const handleInputChange = (section: string, field: string, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value,
      },
    }));
  };

  const handleNestedChange = (
    section: string,
    parent: string,
    field: string,
    value: any,
  ) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [parent]: {
          ...(prev[section as keyof typeof prev] as any)[parent],
          [field]: value,
        },
      },
    }));
  };

  const saveSettings = () => {
    // Simulate saving settings
    alert("Settings saved successfully!");
  };

  const resetSettings = () => {
    if (confirm("Are you sure you want to reset all settings to default?")) {
      // Reset to default values
      alert("Settings reset to default values!");
    }
  };

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = "platform-settings.json";
    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Platform Settings
          </h1>
          <p className="text-gray-600 mt-1">
            Configure platform-wide settings and preferences
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <button
            onClick={exportSettings}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-download-fill mr-2"></i>
            Export
          </button>
          <button
            onClick={resetSettings}
            className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-restart-fill mr-2"></i>
            Reset
          </button>
          <button
            onClick={saveSettings}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-save-fill mr-2"></i>
            Save Changes
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: "general", name: "General", icon: "ri-settings-3-fill" },
              {
                id: "payments",
                name: "Payments",
                icon: "ri-money-dollar-circle-fill",
              },
              {
                id: "notifications",
                name: "Notifications",
                icon: "ri-notification-3-fill",
              },
              {
                id: "security",
                name: "Security",
                icon: "ri-shield-check-fill",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* General Settings */}
          {activeTab === "general" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                General Platform Settings
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform Name
                  </label>
                  <input
                    type="text"
                    value={settings.general.platformName}
                    onChange={(e) =>
                      handleInputChange(
                        "general",
                        "platformName",
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Support Email
                  </label>
                  <input
                    type="email"
                    value={settings.general.supportEmail}
                    onChange={(e) =>
                      handleInputChange(
                        "general",
                        "supportEmail",
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Support Phone
                  </label>
                  <input
                    type="tel"
                    value={settings.general.supportPhone}
                    onChange={(e) =>
                      handleInputChange(
                        "general",
                        "supportPhone",
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    value={settings.general.currency}
                    onChange={(e) =>
                      handleInputChange("general", "currency", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm pr-8"
                  >
                    <option value="NGN">Nigerian Naira (NGN)</option>
                    <option value="USD">US Dollar (USD)</option>
                    <option value="GBP">British Pound (GBP)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={settings.general.timezone}
                    onChange={(e) =>
                      handleInputChange("general", "timezone", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm pr-8"
                  >
                    <option value="Africa/Lagos">Africa/Lagos (WAT)</option>
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">
                      America/New_York (EST)
                    </option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform Description
                </label>
                <textarea
                  value={settings.general.platformDescription}
                  onChange={(e) =>
                    handleInputChange(
                      "general",
                      "platformDescription",
                      e.target.value,
                    )
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      Maintenance Mode
                    </h4>
                    <p className="text-sm text-gray-500">
                      Put the platform in maintenance mode
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleInputChange(
                        "general",
                        "maintenanceMode",
                        !settings.general.maintenanceMode,
                      )
                    }
                    className={`w-12 h-6 rounded-full transition-colors ${
                      settings.general.maintenanceMode
                        ? "bg-red-500"
                        : "bg-gray-300"
                    } relative`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.general.maintenanceMode
                          ? "translate-x-6"
                          : "translate-x-0.5"
                      } absolute top-0.5`}
                    ></div>
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      User Registration
                    </h4>
                    <p className="text-sm text-gray-500">
                      Allow new user registrations
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleInputChange(
                        "general",
                        "userRegistration",
                        !settings.general.userRegistration,
                      )
                    }
                    className={`w-12 h-6 rounded-full transition-colors ${
                      settings.general.userRegistration
                        ? "bg-purple-500"
                        : "bg-gray-300"
                    } relative`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                        settings.general.userRegistration
                          ? "translate-x-6"
                          : "translate-x-0.5"
                      } absolute top-0.5`}
                    ></div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Settings */}
          {activeTab === "payments" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Payment Configuration
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Wallet Funding (₦)
                  </label>
                  <input
                    type="number"
                    value={settings.payments.minimumWalletFunding}
                    onChange={(e) =>
                      handleInputChange(
                        "payments",
                        "minimumWalletFunding",
                        Number(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Wallet Funding (₦)
                  </label>
                  <input
                    type="number"
                    value={settings.payments.maximumWalletFunding}
                    onChange={(e) =>
                      handleInputChange(
                        "payments",
                        "maximumWalletFunding",
                        Number(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Fee (%)
                  </label>
                  <input
                    type="number"
                    value={settings.payments.transactionFee}
                    onChange={(e) =>
                      handleInputChange(
                        "payments",
                        "transactionFee",
                        Number(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="0"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Policy (hours)
                  </label>
                  <input
                    type="number"
                    value={settings.payments.refundPolicy}
                    onChange={(e) =>
                      handleInputChange(
                        "payments",
                        "refundPolicy",
                        Number(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="1"
                  />
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">
                  Payment Gateways
                </h4>
                <div className="space-y-4">
                  {Object.entries(settings.payments.paymentGateways).map(
                    ([gateway, enabled]) => (
                      <div
                        key={gateway}
                        className="flex items-center justify-between"
                      >
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 capitalize">
                            {gateway}
                          </h5>
                          <p className="text-sm text-gray-500">
                            Enable {gateway} payment gateway
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            handleNestedChange(
                              "payments",
                              "paymentGateways",
                              gateway,
                              !enabled,
                            )
                          }
                          className={`w-12 h-6 rounded-full transition-colors ${
                            enabled ? "bg-purple-500" : "bg-gray-300"
                          } relative`}
                        >
                          <div
                            className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                              enabled ? "translate-x-6" : "translate-x-0.5"
                            } absolute top-0.5`}
                          ></div>
                        </button>
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Auto Process Payments
                  </h4>
                  <p className="text-sm text-gray-500">
                    Automatically process successful payments
                  </p>
                </div>
                <button
                  onClick={() =>
                    handleInputChange(
                      "payments",
                      "autoProcessPayments",
                      !settings.payments.autoProcessPayments,
                    )
                  }
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.payments.autoProcessPayments
                      ? "bg-purple-500"
                      : "bg-gray-300"
                  } relative`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                      settings.payments.autoProcessPayments
                        ? "translate-x-6"
                        : "translate-x-0.5"
                    } absolute top-0.5`}
                  ></div>
                </button>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          {activeTab === "notifications" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Notification Preferences
              </h3>

              <div className="space-y-4">
                {Object.entries(settings.notifications).map(
                  ([key, enabled]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 capitalize">
                          {key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {key === "emailNotifications" &&
                            "Send notifications via email"}
                          {key === "smsNotifications" &&
                            "Send notifications via SMS"}
                          {key === "pushNotifications" &&
                            "Send push notifications"}
                          {key === "transactionAlerts" &&
                            "Alert users about transactions"}
                          {key === "maintenanceAlerts" &&
                            "Send maintenance notifications"}
                          {key === "marketingEmails" &&
                            "Send promotional emails"}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          handleInputChange("notifications", key, !enabled)
                        }
                        className={`w-12 h-6 rounded-full transition-colors ${
                          enabled ? "bg-purple-500" : "bg-gray-300"
                        } relative`}
                      >
                        <div
                          className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                            enabled ? "translate-x-6" : "translate-x-0.5"
                          } absolute top-0.5`}
                        ></div>
                      </button>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === "security" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Security Configuration
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.security.sessionTimeout}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "sessionTimeout",
                        Number(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="5"
                    max="120"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password Expiry (days)
                  </label>
                  <input
                    type="number"
                    value={settings.security.passwordExpiry}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "passwordExpiry",
                        Number(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="30"
                    max="365"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Login Attempts
                  </label>
                  <input
                    type="number"
                    value={settings.security.loginAttempts}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "loginAttempts",
                        Number(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="3"
                    max="10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Backup Frequency
                  </label>
                  <select
                    value={settings.security.backupFrequency}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "backupFrequency",
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm pr-8"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IP Whitelist
                </label>
                <textarea
                  value={settings.security.ipWhitelist}
                  onChange={(e) =>
                    handleInputChange("security", "ipWhitelist", e.target.value)
                  }
                  placeholder="Enter IP addresses separated by commas (optional)"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Two-Factor Authentication
                  </h4>
                  <p className="text-sm text-gray-500">
                    Require 2FA for admin accounts
                  </p>
                </div>
                <button
                  onClick={() =>
                    handleInputChange(
                      "security",
                      "twoFactorAuth",
                      !settings.security.twoFactorAuth,
                    )
                  }
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.security.twoFactorAuth
                      ? "bg-purple-500"
                      : "bg-gray-300"
                  } relative`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                      settings.security.twoFactorAuth
                        ? "translate-x-6"
                        : "translate-x-0.5"
                    } absolute top-0.5`}
                  ></div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
